import React, { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { EmployeeAvatar, MaybeTooltip } from '@modernloop/shared/components';
import { UnknownUserIcon } from '@modernloop/shared/icons';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Box, Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  EmployeeFragment,
  EmployeeFragmentDoc,
  ScheduleTaskAssignee_TaskFragment,
  SourceType,
  useTaskSetAssigneeMutation,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import {
  selectButtonLabelSxProps_DO_NOT_USE,
  selectButtonProps_DO_NOT_USE,
} from 'src/constants/selectButtonProps_DO_NOT_USE';

import { useSubscribersSelectRefetch } from 'src/entities/SubscribersSelect/useSubscribersSelectRefetch';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';

import AssigneeSelect from 'src/views-new/ScheduleTask/ScheduleTaskDetails/Common/ScheduleTaskAssignee/AssigneeSelect';
import { unassignedAssigneeSelectButtonSx } from 'src/views-new/ScheduleTask/ScheduleTaskDetails/alignment';
import useMySchedulingTasksRefetch from 'src/views-new/ScheduleTaskDashboard/ScheduleTaskDashboardTables/useMySchedulingTasksRefetch';
import useScheduleTaskDashboardTaskSearchRefetch from 'src/views-new/ScheduleTaskDashboard/ScheduleTaskDashboardTables/useScheduleTaskDashboardTaskSearchRefetch';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskSetAsigneeMutation = gql`
  ${EmployeeFragmentDoc}
  mutation TaskSetAssignee($input: TaskSetAssigneeInput!) {
    taskSetAssignee(input: $input) {
      id
      assignee {
        ...Employee
      }
    }
  }
`;

interface Fragments {
  task: ScheduleTaskAssignee_TaskFragment;
}

interface Props {
  sx?: SxProps<Theme>;
  onClick?: (e) => void;
  shouldRefetchSubscribersList?: boolean;
  shouldRefecthTaskSearch?: boolean;
  shouldRefetchMySchedulingTasks?: boolean;
}

const ScheduleTaskAssignee: FCWithFragments<Fragments, Props> = ({
  sx,
  task,
  onClick,
  shouldRefetchSubscribersList,
  shouldRefecthTaskSearch,
  shouldRefetchMySchedulingTasks,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const refetchActivityFeed = useActivityFeedRefetch(SourceType.SourceTypeTask);
  const refetchSubscriberSelect = useSubscribersSelectRefetch();
  const refetchScheduleTaskDashboardTaskSearch = useScheduleTaskDashboardTaskSearchRefetch();
  const refetchMySchedulingTasks = useMySchedulingTasksRefetch();
  const [taskSetAssigneeMutation] = useTaskSetAssigneeMutation();

  const handleAssigneeChange = useCallback(
    (assignee: EmployeeFragment) => {
      taskSetAssigneeMutation({
        variables: {
          input: {
            taskId: task.id,
            assigneeEmployeeId: assignee.id,
          },
        },
        optimisticResponse: {
          taskSetAssignee: {
            __typename: 'Task',
            id: task.id,
            assignee: assignee.id === null ? null : assignee,
          },
        },
      })
        .then(() => {
          if (shouldRefetchSubscribersList) {
            refetchSubscriberSelect();
          }
          if (shouldRefetchMySchedulingTasks) {
            refetchMySchedulingTasks();
          }
          // eslint-disable-next-line promise/always-return
          if (shouldRefecthTaskSearch) {
            refetchScheduleTaskDashboardTaskSearch();
          }
          refetchActivityFeed();
        })
        .catch((e) => {
          enqueueSnackbar(getExternalErrorMessage(e), { variant: 'error' });
        });
    },
    [
      enqueueSnackbar,
      refetchActivityFeed,
      refetchMySchedulingTasks,
      refetchScheduleTaskDashboardTaskSearch,
      refetchSubscriberSelect,
      shouldRefecthTaskSearch,
      shouldRefetchMySchedulingTasks,
      shouldRefetchSubscribersList,
      task.id,
      taskSetAssigneeMutation,
    ]
  );

  return (
    <Box sx={{ ...sx, maxWidth: '100%' }} onClick={onClick}>
      <AssigneeSelect
        onChange={(newMember) => handleAssigneeChange(newMember?.employee as EmployeeFragment)}
        getLabel={() => {
          let label;
          let startIcon;

          if (!task.assignee) {
            label = (
              <Typography sx={{ ...selectButtonLabelSxProps_DO_NOT_USE, paddingRight: 3 }}>
                {t('hiring-dashboard-tasks:tasks.create.unassigned')}
              </Typography>
            );
            startIcon = <UnknownUserIcon />;
          } else {
            label = (
              <Stack direction="row" spacing={1} sx={{ ...selectButtonLabelSxProps_DO_NOT_USE, paddingRight: 3 }}>
                <Box
                  sx={{
                    marginLeft: '-4px',
                  }}
                >
                  <EmployeeAvatar
                    imageUrl={task.assignee.internalProfilePictureUrl}
                    fullName={task.assignee.fullName}
                    size={20}
                  />
                </Box>

                <Box
                  overflow="ellipsis"
                  sx={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <MaybeTooltip label={task.assignee.fullName ?? ''} title={task.assignee.fullName ?? ''} />
                </Box>
              </Stack>
            );
          }

          return (
            <Button
              {...selectButtonProps_DO_NOT_USE}
              variant="text"
              startIcon={startIcon}
              sx={{
                ...selectButtonProps_DO_NOT_USE.sx,
                ...(!task.assignee ? unassignedAssigneeSelectButtonSx : {}),
              }}
            >
              {label}
            </Button>
          );
        }}
      />
    </Box>
  );
};

ScheduleTaskAssignee.fragments = {
  task: gql`
    fragment ScheduleTaskAssignee_task on Task {
      id
      assignee {
        id
        fullName
        internalProfilePictureUrl
      }
    }
  `,
};

export default ScheduleTaskAssignee;
